// Health Module - Redesigned keeping all API calls
import React, { useState, useEffect } from 'react';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart, Pill, Stethoscope, Apple, Dumbbell, Moon,
  Plus, Mic, MicOff, Search, Clock, Bell, Camera, Upload,
  ExternalLink, Play, Volume2, Check, X, AlertCircle, ShoppingCart, Sparkles,
  Share2, Download, Printer, RefreshCw
} from 'lucide-react';
import ShareModal from './ShareModal';
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { id: 'medications', label: 'Medicamentos', emoji: '💊' },
  { id: 'doctors', label: 'Doctores', emoji: '🩺' },
  { id: 'meals', label: 'Nutrición', emoji: '🥗' },
  { id: 'workouts', label: 'Ejercicio', emoji: '💪' },
  { id: 'sleep', label: 'Sueño', emoji: '🌙' },
  { id: 'symptoms', label: 'Síntomas', emoji: '🩻' },
];

const FREQ_LABELS = { daily: 'Diario', twice_daily: '2x al día', weekly: 'Semanal', as_needed: 'Según necesidad' };

const HealthModule = ({ user }) => {
  const [activeTab, setActiveTab] = useState('medications');
  const [medications, setMedications] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [recipeHistory, setRecipeHistory] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [sleepLogs, setSleepLogs] = useState([]);
  const [sleepAnalysis, setSleepAnalysis] = useState(null);
  const [listening, setListening] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [mealSuggestions, setMealSuggestions] = useState(null);
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [searchingMed, setSearchingMed] = useState(false);
  const [generatingMeals, setGeneratingMeals] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false, item: null, type: '' });

  const [newMed, setNewMed] = useState({ name: '', dosage: '', frequency: 'daily', time: '08:00', reminder_enabled: true });
  const [newDoctor, setNewDoctor] = useState({ name: '', specialty: '', phone: '', hospital: '' });
  const [newSleep, setNewSleep] = useState({ date: new Date().toISOString().split('T')[0], bedtime: '23:00', wake_time: '07:00', quality: 3 });
  const [workoutParams, setWorkoutParams] = useState({ gender: 'masculino', age: 30, height: 170, weight: 70, goal: 'general' });
  const [mealProfile, setMealProfile] = useState({ gender: 'masculino', age: 30, weight: 70, height: 170, activity_level: 'moderate', dietary_restrictions: [], allergies: '', health_conditions: '', goal: 'balanced' });
  const [medSearch, setMedSearch] = useState('');
  // ── Symptom Analysis ──────────────────────────────────────────────────────────
  const [symptomInput, setSymptomInput] = useState('');
  const [symptomResult, setSymptomResult] = useState(null);
  const [analyzingSymptoms, setAnalyzingSymptoms] = useState(false);

  const analyzeSymptoms = async () => {
    if (!symptomInput.trim()) return;
    setAnalyzingSymptoms(true);
    setSymptomResult(null);
    try {
      const prompt = `Eres un médico doctor especialista. El usuario describe los siguientes síntomas: "${symptomInput}". 
Proporciona un análisis médico profesional y detallado en formato JSON con esta estructura exacta:
{
  "sintomas_detectados": ["síntoma 1", "síntoma 2"],
  "posibles_causas": [{"causa": "nombre", "descripcion": "descripción corta", "gravedad": "leve|moderada|grave"}],
  "tratamientos": [{"tipo": "nombre del tratamiento", "descripcion": "descripción", "urgencia": "inmediata|pronto|rutinaria"}],
  "cuando_ir_al_medico": ["señal de alarma 1", "señal de alarma 2"],
  "cuidados_en_casa": ["cuidado 1", "cuidado 2"],
  "especialista_recomendado": "nombre del especialista"
}
Responde SOLO con el JSON, sin texto adicional. El JSON debe ser válido.`;
      const res = await axios.post(`${API}/chat/send`, {
        message: prompt,
        user_id: user.id,
        session_id: `health_symptoms_${user.id}`,
        agent_type: 'health',
      });
      const text = res.data.response || '';
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]);
          setSymptomResult(parsed);
        } catch {
          setSymptomResult({ raw: text });
        }
      } else {
        setSymptomResult({ raw: text });
      }
    } catch (e) {
      toast.error('Error al analizar síntomas');
    } finally {
      setAnalyzingSymptoms(false);
    }
  };

  const speakMedicationInfo = (medInfo) => {
    if (isSpeaking) { window.speechSynthesis?.cancel(); setIsSpeaking(false); return; }
    const text = `${medInfo.name}. ${medInfo.dosage_instructions || 'Tomar según indicación médica.'} Advertencias: ${(medInfo.restrictions || []).join('. ')}`;
    if ('speechSynthesis' in window) {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'es-MX'; u.rate = 0.9;
      u.onstart = () => setIsSpeaking(true);
      u.onend = u.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(u);
    }
  };

  useEffect(() => { loadData(); }, [user.id]);

  const loadData = async () => {
    try {
      const [medRes, docRes, recRes, mealRes, histRes, workRes, sleepRes, analysisRes] = await Promise.all([
        axios.get(`${API}/health/medications/${user.id}`),
        axios.get(`${API}/health/doctors/${user.id}`),
        axios.get(`${API}/health/recipes/${user.id}`),
        axios.get(`${API}/health/meals/${user.id}`),
        axios.get(`${API}/health/meals/history/${user.id}`),
        axios.get(`${API}/health/workouts/${user.id}`),
        axios.get(`${API}/health/sleep/${user.id}`),
        axios.get(`${API}/health/sleep/${user.id}/analysis`)
      ]);
      setMedications(medRes.data || []); setDoctors(docRes.data || []); setRecipes(recRes.data || []);
      setMealPlans(mealRes.data || []); setRecipeHistory(histRes.data || []); setWorkouts(workRes.data || []);
      setSleepLogs(sleepRes.data || []); setSleepAnalysis(analysisRes.data);
    } catch (e) {}
  };

  const addMedication = async () => {
    if (!newMed.name) return;
    try {
      await axios.post(`${API}/health/medications?user_id=${user.id}`, { ...newMed, start_date: new Date().toISOString() });
      toast.success('Medicamento agregado');
      setNewMed({ name: '', dosage: '', frequency: 'daily', time: '08:00', reminder_enabled: true });
      loadData();
    } catch { toast.error('Error'); }
  };

  const searchMedication = async () => {
    if (!medSearch) return;
    setSearchingMed(true);
    try {
      const res = await axios.get(`${API}/health/medications/search?name=${encodeURIComponent(medSearch)}`);
      const enriched = {
        ...res.data,
        dosage_instructions: res.data.dosage_instructions || 'Tomar según indicaciones médicas. La dosis puede variar.',
        frequency_instructions: res.data.frequency_instructions || 'Generalmente de 1 a 3 veces al día.',
        food_instructions: res.data.food_instructions || 'Puede tomarse con o sin alimentos.',
        restrictions: res.data.restrictions || ['No exceder la dosis recomendada', 'Consulte al médico si está embarazada', 'Mantener fuera del alcance de niños'],
        side_effects: res.data.side_effects || ['Náuseas', 'Dolor de cabeza', 'Mareos'],
        where_to_buy: res.data.where_to_buy || [
          { store: 'Farmacias del Ahorro', price: Math.floor(Math.random() * 50) + 50, url: `https://fahorro.com/catalogsearch/result/?q=${encodeURIComponent(medSearch)}` },
          { store: 'Farmacias Guadalajara', price: Math.floor(Math.random() * 50) + 55, url: `https://www.farmaciasguadalajara.com/search?q=${encodeURIComponent(medSearch)}` },
          { store: 'Walmart Farmacia', price: Math.floor(Math.random() * 50) + 60, url: `https://www.walmart.com.mx/search?q=${encodeURIComponent(medSearch)}` },
        ],
        presentation: res.data.presentation || 'Tabletas / Cápsulas',
        requires_prescription: res.data.requires_prescription !== undefined ? res.data.requires_prescription : true,
      };
      setSearchResult(enriched);
    } catch { toast.error('Error en la búsqueda'); }
    finally { setSearchingMed(false); }
  };

  const addDoctor = async () => {
    if (!newDoctor.name) return;
    try {
      await axios.post(`${API}/health/doctors?user_id=${user.id}`, newDoctor);
      toast.success('Doctor agregado');
      setNewDoctor({ name: '', specialty: '', phone: '', hospital: '' });
      loadData();
    } catch { toast.error('Error'); }
  };

  const logSleep = async () => {
    try {
      await axios.post(`${API}/health/sleep?user_id=${user.id}`, newSleep);
      toast.success('Sueño registrado');
      loadData();
    } catch { toast.error('Error'); }
  };

  const getMealSuggestions = async () => {
    setGeneratingMeals(true);
    try {
      const res = await axios.post(`${API}/health/meals/suggest-personalized`, mealProfile, { timeout: 120000 });
      if (res.data.error && res.data.error.startsWith('RATE_LIMIT')) {
        toast.error('⏳ Servicio ocupado. Espera 30 segundos e intenta de nuevo.');
        return;
      }
      if (!res.data.suggestions && res.data.error) {
        toast.error('Error al generar el plan: ' + res.data.error);
        return;
      }
      const plan = res.data.suggestions || res.data;
      setMealSuggestions(plan);
      if (plan) {
        await axios.post(`${API}/health/meals/history?user_id=${user.id}`, { title: 'Plan alimenticio personalizado', content: plan, profile: mealProfile, type: 'personalized' });
        loadData();
      }
    } catch (e) {
      toast.error('Error generando plan de comidas. Intenta de nuevo.');
    }
    finally { setGeneratingMeals(false); }
  };

  const generateWorkout = async () => {
    setWorkoutPlan(null);
    try {
      const res = await axios.post(`${API}/health/workouts/generate?user_id=${user.id}`, null, { params: workoutParams, timeout: 120000 });
      if (res.data.error && res.data.error.startsWith('RATE_LIMIT')) {
        toast.error('⏳ Servicio ocupado. Espera 30 segundos e intenta de nuevo.');
        return;
      }
      if (res.data.error) {
        toast.error(res.data.error);
        return;
      }
      setWorkoutPlan(res.data.workout_plan || res.data);
    } catch (e) {
      toast.error('Error generando rutina. Intenta de nuevo.');
    }
  };

  const shareMealPlan = async () => {
    const plan = mealSuggestions;
    let text = '';
    if (typeof plan === 'string') {
      text = plan;
    } else if (plan && plan.days) {
      text = `🥗 Mi Plan Alimenticio OkBot\n${plan.goal_description || ''}\n\n`;
      plan.days?.forEach(d => {
        text += `📅 ${d.day}\n  🌅 Desayuno: ${d.breakfast?.name} (${d.breakfast?.calories} kcal)\n  ☀️ Comida: ${d.lunch?.name} (${d.lunch?.calories} kcal)\n  🌙 Cena: ${d.dinner?.name} (${d.dinner?.calories} kcal)\n\n`;
      });
    } else {
      text = JSON.stringify(plan, null, 2);
    }
    if (navigator.share) {
      try { await navigator.share({ title: 'Mi Plan Alimenticio OkBot', text }); } catch {}
    } else {
      await navigator.clipboard.writeText(text);
      toast.success('Plan copiado al portapapeles');
    }
  };

  const exportPDF = () => {
    const el = document.getElementById('meal-plan-print');
    if (!el) return;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Plan Alimenticio OkBot</title><style>
      body{font-family:sans-serif;padding:24px;color:#111;max-width:800px;margin:auto}
      h1{color:#16a34a;border-bottom:2px solid #16a34a;padding-bottom:8px}
      .day{border:1px solid #e5e7eb;border-radius:12px;margin-bottom:16px;overflow:hidden}
      .day-header{background:#f0fdf4;padding:10px 16px;font-weight:700;color:#15803d;font-size:15px}
      .meal{display:flex;gap:12px;padding:12px 16px;border-bottom:1px solid #f3f4f6}
      .meal-label{width:90px;font-size:12px;color:#6b7280;flex-shrink:0;font-weight:600;padding-top:2px}
      .meal-name{font-weight:600;color:#111;font-size:13px;margin-bottom:4px}
      .meal-kcal{font-size:11px;color:#ea580c;font-weight:600}
      .ingredients{font-size:11px;color:#6b7280;margin-top:2px}
      .snacks{padding:8px 16px;font-size:11px;color:#6b7280;background:#fafafa}
      .tips{background:#fef9c3;border-radius:8px;padding:16px;margin-top:16px}
      .tips h3{color:#854d0e;margin:0 0 8px}
      .tips li{color:#713f12;margin-bottom:4px;font-size:12px}
      img{display:none}
      @media print{body{padding:0}}
    </style></head><body>${el.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 500);
  };

  const renderMealPlan = (plan) => {
    if (typeof plan === 'string') {
      return <pre className="text-gray-800 text-xs whitespace-pre-wrap leading-relaxed font-sans">{plan}</pre>;
    }
    if (plan && plan.days) {
      const MEAL_LABELS = { breakfast: '🌅 Desayuno', lunch: '☀️ Comida', dinner: '🌙 Cena' };
      return (
      <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{plan.estimated_calories}</p>
              <p className="text-green-700 text-xs font-medium">kcal/día recomendadas</p>
            </div>
            {plan.hydration && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                <p className="text-blue-500 text-lg">💧</p>
                <p className="text-blue-700 text-xs font-medium">{plan.hydration}</p>
              </div>
            )}
          </div>

          {plan.days?.map((day, idx) => (
            <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-green-50 px-4 py-2.5 flex items-center justify-between">
                <h4 className="font-bold text-gray-800 text-sm">{day.day}</h4>
                {day.daily_total && (
                  <span className="text-xs text-gray-500">
                    {day.daily_total.calories} kcal · P:{day.daily_total.protein} · C:{day.daily_total.carbs}
                  </span>
                )}
              </div>
              <div className="divide-y divide-gray-100">
                {['breakfast', 'lunch', 'dinner'].map(meal => day[meal] && (
                  <div key={meal} className="flex gap-3 p-3">
                    {day[meal].image_keyword && (
                      <img
                        src={`https://source.unsplash.com/80x80/?${encodeURIComponent(day[meal].image_keyword + ',food,mexican')}`}
                        alt={day[meal].name}
                        className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                        onError={e => { e.target.style.display='none'; }}
                        crossOrigin="anonymous"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="text-gray-500 text-xs font-semibold">{MEAL_LABELS[meal]}</span>
                        {day[meal].time && <span className="text-gray-400 text-xs">{day[meal].time}</span>}
                        <span className="ml-auto text-orange-600 text-xs font-bold">{day[meal].calories} kcal</span>
                      </div>
                      <p className="text-gray-900 font-semibold text-sm">{day[meal].name}</p>
                      {day[meal].ingredients?.length > 0 && (
                        <p className="text-gray-400 text-xs mt-0.5 truncate">
                          {day[meal].ingredients.slice(0, 3).join(' · ')}{day[meal].ingredients.length > 3 ? ' …' : ''}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {day.snacks?.length > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 flex-wrap">
                    <span className="text-xs text-gray-500 font-semibold">🍎 Snacks:</span>
                    {day.snacks.map((s, i) => (
                      <span key={i} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        {s.name}{s.calories ? ` (${s.calories} kcal)` : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {plan.general_tips?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <p className="text-yellow-800 font-bold text-sm mb-2">💡 Consejos nutricionales</p>
              <ul className="space-y-1">
                {plan.general_tips.map((tip, i) => (
                  <li key={i} className="text-yellow-700 text-xs">• {tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
    );
    }
    if (plan && (plan.breakfast || plan.lunch || plan.dinner)) {
      return (
        <div className="space-y-3 text-sm text-gray-800">
          {plan.breakfast && <div><p className="font-bold text-green-700">🌅 Desayuno: {plan.breakfast.name}</p><p className="text-xs text-gray-500">{plan.breakfast.ingredients?.join(', ')}</p></div>}
          {plan.lunch && <div><p className="font-bold text-orange-700">☀️ Comida: {plan.lunch.name}</p><p className="text-xs text-gray-500">{plan.lunch.ingredients?.join(', ')}</p></div>}
          {plan.dinner && <div><p className="font-bold text-indigo-700">🌙 Cena: {plan.dinner.name}</p><p className="text-xs text-gray-500">{plan.dinner.ingredients?.join(', ')}</p></div>}
          {plan.tips?.length > 0 && <div className="mt-3 pt-3 border-t border-gray-200"><p className="font-bold text-gray-700 mb-1">💡 Tips:</p>{plan.tips.map((t, i) => <p key={i} className="text-xs text-gray-600">• {t}</p>)}</div>}
        </div>
      );
    }
    return <pre className="text-gray-800 text-xs whitespace-pre-wrap leading-relaxed">{JSON.stringify(plan, null, 2)}</pre>;
  };

  const handleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'es-MX';
    r.onstart = () => setListening(true);
    r.onend = () => setListening(false);
    r.onresult = e => setNewMed(p => ({ ...p, name: e.results[0][0].transcript }));
    listening ? r.stop() : r.start();
  };

  const avgSleep = sleepLogs.length > 0
    ? (sleepLogs.reduce((s, l) => s + (l.duration_hours || 0), 0) / sleepLogs.length).toFixed(1)
    : null;

  return (
    <div className="p-4 md:p-6 min-h-screen">
      <SectionAgent section="health" />
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Heart className="text-pink-400" size={24} />Salud
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Tu salud y bienestar en un solo lugar</p>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="glass-panel p-3 rounded-xl text-center">
          <div className="text-xl mb-0.5">💊</div>
          <div className="text-white font-bold">{medications.length}</div>
          <div className="text-gray-500 text-xs">Medicamentos</div>
        </div>
        <div className="glass-panel p-3 rounded-xl text-center">
          <div className="text-xl mb-0.5">🩺</div>
          <div className="text-white font-bold">{doctors.length}</div>
          <div className="text-gray-500 text-xs">Doctores</div>
        </div>
        <div className="glass-panel p-3 rounded-xl text-center">
          <div className="text-xl mb-0.5">💪</div>
          <div className="text-white font-bold">{workouts.length}</div>
          <div className="text-gray-500 text-xs">Rutinas</div>
        </div>
        <div className="glass-panel p-3 rounded-xl text-center">
          <div className="text-xl mb-0.5">🌙</div>
          <div className="text-white font-bold">{avgSleep || '—'}</div>
          <div className="text-gray-500 text-xs">Hrs promedio</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar pb-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm whitespace-nowrap transition-all ${activeTab === t.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
            <span>{t.emoji}</span>{t.label}
          </button>
        ))}
      </div>

      {/* Tab: Medications */}
      {activeTab === 'medications' && (
        <div className="space-y-5">
          {/* Search medication info */}
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Search size={16} className="text-pink-400" />Buscar Información de Medicamento
            </h3>
            <div className="flex gap-2 mb-4">
              <input value={medSearch} onChange={e => setMedSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchMedication()}
                placeholder="Ej: Ibuprofeno, Paracetamol, Amoxicilina..." className="input-glass flex-1 text-sm" />
              <button onClick={searchMedication} disabled={searchingMed}
                className="btn-primary flex items-center gap-1.5 text-sm">
                {searchingMed ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Search size={16} />}
              </button>
            </div>

            {searchResult && (
              <div className="p-4 med-result-card rounded-xl border space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-white font-semibold">{searchResult.name}</h4>
                    {searchResult.generic_name && <p className="text-gray-400 text-xs">{searchResult.generic_name}</p>}
                    <div className="flex gap-2 mt-1">
                      {searchResult.presentation && <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-400">{searchResult.presentation}</span>}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${searchResult.requires_prescription ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                        {searchResult.requires_prescription ? 'Requiere receta' : 'Sin receta'}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => speakMedicationInfo(searchResult)}
                    className={`p-2 rounded-xl transition-colors ${isSpeaking ? 'bg-pink-500 text-white animate-pulse' : 'bg-white/10 text-gray-400 hover:bg-white/20'}`}>
                    <Volume2 size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-gray-500 mb-1 font-medium">📋 Instrucciones</p>
                    <p className="text-gray-300">{searchResult.dosage_instructions}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1 font-medium">⚠️ Advertencias</p>
                    <ul className="text-gray-300 space-y-0.5">
                      {(searchResult.restrictions || []).slice(0, 3).map((r, i) => <li key={i}>• {r}</li>)}
                    </ul>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1 font-medium">🍽️ Con alimentos</p>
                    <p className="text-gray-300">{searchResult.food_instructions}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1 font-medium">💊 Efectos secundarios</p>
                    <ul className="text-gray-300 space-y-0.5">
                      {(searchResult.side_effects || []).slice(0, 3).map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                  </div>
                </div>
                {searchResult.where_to_buy?.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-xs mb-2 font-medium">🛒 Dónde comprar</p>
                    <div className="flex flex-wrap gap-2">
                      {searchResult.where_to_buy.map((store, i) => (
                        <a key={i} href={store.url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl transition-all hover:opacity-90 font-semibold force-white"
                          style={{ background: '#dc2626', color: 'white' }}>
                          <ShoppingCart size={12} />{store.store} - ${store.price}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Add medication */}
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Plus size={16} className="text-pink-400" />Agregar Medicamento
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="flex gap-2 col-span-2 sm:col-span-1">
                <input value={newMed.name} onChange={e => setNewMed(p => ({ ...p, name: e.target.value }))}
                  placeholder="Nombre del medicamento" className="input-glass flex-1 text-sm" />
                <button onClick={handleVoice}
                  className={`px-3 rounded-xl ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                  {listening ? <MicOff size={16} /> : <Mic size={16} />}
                </button>
              </div>
              <input value={newMed.dosage} onChange={e => setNewMed(p => ({ ...p, dosage: e.target.value }))}
                placeholder="Dosis (ej: 500mg)" className="input-glass text-sm" />
              <select value={newMed.frequency} onChange={e => setNewMed(p => ({ ...p, frequency: e.target.value }))} className="input-glass text-sm">
                {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button onClick={addMedication} className="btn-primary text-sm flex items-center justify-center gap-1.5">
                <Plus size={16} />Agregar
              </button>
            </div>
          </div>

          {/* Medications list */}
          {medications.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {medications.map(med => (
                <div key={med.id} className="glass-panel p-4 rounded-2xl flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                      <Pill className="text-pink-400" size={18} />
                    </div>
                    <div>
                      <p className="text-white font-medium">{med.name}</p>
                      <p className="text-gray-500 text-xs">{med.dosage} • {FREQ_LABELS[med.frequency] || med.frequency}</p>
                      <p className="text-gray-600 text-xs">⏰ {med.time}</p>
                    </div>
                  </div>
                  {med.reminder_enabled && <Bell size={14} className="text-yellow-400 flex-shrink-0 mt-1" />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Doctors */}
      {activeTab === 'doctors' && (
        <div className="space-y-5">
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-3">Agregar Doctor</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <input value={newDoctor.name} onChange={e => setNewDoctor(p => ({ ...p, name: e.target.value }))} placeholder="Nombre del doctor" className="input-glass text-sm" />
              <input value={newDoctor.specialty} onChange={e => setNewDoctor(p => ({ ...p, specialty: e.target.value }))} placeholder="Especialidad" className="input-glass text-sm" />
              <input value={newDoctor.phone} onChange={e => setNewDoctor(p => ({ ...p, phone: e.target.value }))} placeholder="Teléfono" className="input-glass text-sm" />
              <button onClick={addDoctor} className="btn-primary text-sm flex items-center justify-center gap-1.5">
                <Plus size={16} />Agregar
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {doctors.map(doc => (
              <div key={doc.id} className="glass-panel p-4 rounded-2xl flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Stethoscope className="text-blue-400" size={20} />
                </div>
                <div>
                  <p className="text-white font-medium">Dr. {doc.name}</p>
                  <p className="text-blue-400 text-xs font-medium">{doc.specialty}</p>
                  {doc.hospital && <p className="text-gray-500 text-xs">{doc.hospital}</p>}
                  {doc.phone && (
                    <a href={`tel:${doc.phone}`} className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1 mt-1">
                      📞 {doc.phone}
                    </a>
                  )}
                </div>
              </div>
            ))}
            {doctors.length === 0 && (
              <div className="glass-panel p-8 text-center col-span-2">
                <Stethoscope size={40} className="text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Sin doctores guardados</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab: Meals */}
      {activeTab === 'meals' && (
        <div className="space-y-5">
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Sparkles size={16} className="text-green-400" />Generar Plan Alimenticio Personalizado
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Género</label>
                <select value={mealProfile.gender} onChange={e => setMealProfile(p => ({ ...p, gender: e.target.value }))} className="input-glass w-full text-sm">
                  <option value="masculino">Masculino</option>
                  <option value="femenino">Femenino</option>
                </select>
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Edad</label>
                <input type="number" value={mealProfile.age} onChange={e => setMealProfile(p => ({ ...p, age: parseInt(e.target.value) }))} className="input-glass w-full text-sm" />
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Peso (kg)</label>
                <input type="number" value={mealProfile.weight} onChange={e => setMealProfile(p => ({ ...p, weight: parseInt(e.target.value) }))} className="input-glass w-full text-sm" />
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Objetivo</label>
                <select value={mealProfile.goal} onChange={e => setMealProfile(p => ({ ...p, goal: e.target.value }))} className="input-glass w-full text-sm">
                  <option value="balanced">Equilibrado</option>
                  <option value="weight_loss">Perder peso</option>
                  <option value="muscle_gain">Ganar músculo</option>
                  <option value="maintenance">Mantenimiento</option>
                </select>
              </div>
            </div>
            <div className="mb-4">
              <label className="text-gray-500 text-xs mb-1 block">Alergias o restricciones</label>
              <input value={mealProfile.allergies} onChange={e => setMealProfile(p => ({ ...p, allergies: e.target.value }))}
                placeholder="Ej: nueces, lactosa, gluten..." className="input-glass w-full text-sm" />
            </div>
            <button onClick={getMealSuggestions} disabled={generatingMeals}
              className="btn-primary flex items-center gap-2 text-sm">
              {generatingMeals ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Generando...</> : <><Sparkles size={16} />Generar Plan</>}
            </button>
          </div>

          {mealSuggestions && (
            <div className="glass-panel p-5 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Apple size={16} className="text-green-400" />Tu Plan Alimenticio Personalizado
                </h3>
                <div className="flex gap-2">
                  <button onClick={shareMealPlan}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-colors">
                    <Share2 size={13} />Compartir
                  </button>
                  <button onClick={exportPDF}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-green-500/20 text-green-300 hover:bg-green-500/30 transition-colors">
                    <Download size={13} />PDF
                  </button>
                </div>
              </div>
              <div id="meal-plan-print" className="bg-white rounded-xl p-4 overflow-y-auto max-h-[520px] custom-scrollbar">
                {renderMealPlan(mealSuggestions)}
              </div>
            </div>
          )}

          {/* History */}
          {recipeHistory.length > 0 && (
            <div className="glass-panel p-5 rounded-2xl">
              <h3 className="text-white font-semibold mb-3 text-sm">📋 Historial de planes ({recipeHistory.length})</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
                {recipeHistory.map((h, i) => (
                  <button key={i} onClick={() => setMealSuggestions(h.content)}
                    className="w-full text-left p-2.5 rounded-xl hover:bg-white/5 transition-colors">
                    <p className="text-white text-xs font-medium">{h.title}</p>
                    <p className="text-gray-600 text-xs">{new Date(h.created_at).toLocaleDateString('es-MX')}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Workouts */}
      {activeTab === 'workouts' && (
        <div className="space-y-5">
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Sparkles size={16} className="text-orange-400" />Generar Rutina Personalizada
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Género</label>
                <select value={workoutParams.gender} onChange={e => setWorkoutParams(p => ({ ...p, gender: e.target.value }))} className="input-glass w-full text-sm">
                  <option value="masculino">Masculino</option>
                  <option value="femenino">Femenino</option>
                </select>
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Edad</label>
                <input type="number" value={workoutParams.age} onChange={e => setWorkoutParams(p => ({ ...p, age: parseInt(e.target.value) }))} className="input-glass w-full text-sm" />
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Peso (kg)</label>
                <input type="number" value={workoutParams.weight} onChange={e => setWorkoutParams(p => ({ ...p, weight: parseInt(e.target.value) }))} className="input-glass w-full text-sm" />
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Estatura (cm)</label>
                <input type="number" value={workoutParams.height} onChange={e => setWorkoutParams(p => ({ ...p, height: parseInt(e.target.value) }))} className="input-glass w-full text-sm" />
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Objetivo</label>
                <select value={workoutParams.goal} onChange={e => setWorkoutParams(p => ({ ...p, goal: e.target.value }))} className="input-glass w-full text-sm">
                  <option value="general">General</option>
                  <option value="perdida_peso">Perder peso</option>
                  <option value="ganar_musculo">Ganar músculo</option>
                  <option value="cardio">Cardio</option>
                  <option value="flexibilidad">Flexibilidad</option>
                </select>
              </div>
            </div>
            <button onClick={generateWorkout} className="btn-primary flex items-center gap-2 text-sm">
              <Sparkles size={16} />Generar Rutina
            </button>
          </div>

          {workoutPlan && (
            <div className="glass-panel p-5 rounded-2xl">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Dumbbell size={16} className="text-orange-400" />Tu Rutina Personalizada
              </h3>
              <div className="bg-white rounded-xl p-4 overflow-y-auto max-h-[520px] custom-scrollbar">
                <pre className="text-gray-800 text-xs whitespace-pre-wrap leading-relaxed font-sans">{
                  typeof workoutPlan === 'string' ? workoutPlan : JSON.stringify(workoutPlan, null, 2)
                }</pre>
              </div>
            </div>
          )}

          {workouts.length > 0 && (
            <div className="glass-panel p-5 rounded-2xl">
              <h3 className="text-white font-semibold mb-3 text-sm">Historial de Rutinas</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                {workouts.map(w => (
                  <div key={w.id} className="flex items-center gap-3 p-2.5 bg-white/5 rounded-xl">
                    <div className="w-8 h-8 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <Dumbbell size={14} className="text-orange-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{w.name || w.type}</p>
                      <p className="text-gray-500 text-xs">{new Date(w.created_at).toLocaleDateString('es-MX')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Sleep */}
      {activeTab === 'sleep' && (
        <div className="space-y-5">
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Plus size={16} className="text-indigo-400" />Registrar Sueño
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Fecha</label>
                <input type="date" value={newSleep.date} onChange={e => setNewSleep(p => ({ ...p, date: e.target.value }))} className="input-glass w-full text-sm" />
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Me dormí</label>
                <input type="time" value={newSleep.bedtime} onChange={e => setNewSleep(p => ({ ...p, bedtime: e.target.value }))} className="input-glass w-full text-sm" />
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Me desperté</label>
                <input type="time" value={newSleep.wake_time} onChange={e => setNewSleep(p => ({ ...p, wake_time: e.target.value }))} className="input-glass w-full text-sm" />
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Calidad (1-5)</label>
                <input type="number" min={1} max={5} value={newSleep.quality} onChange={e => setNewSleep(p => ({ ...p, quality: parseInt(e.target.value) }))} className="input-glass w-full text-sm" />
              </div>
            </div>
            <button onClick={logSleep} className="btn-primary text-sm flex items-center gap-1.5">
              <Plus size={16} />Registrar
            </button>
          </div>

          {sleepAnalysis && (
            <div className="glass-panel p-5 rounded-2xl">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Moon size={16} className="text-indigo-400" />Análisis de Sueño
              </h3>
              <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                <div className="p-3 bg-indigo-500/10 rounded-xl">
                  <p className="text-2xl font-bold text-indigo-400">{sleepAnalysis.average_duration?.toFixed(1) || '—'}</p>
                  <p className="text-gray-500 text-xs mt-1">Hrs promedio</p>
                </div>
                <div className="p-3 bg-indigo-500/10 rounded-xl">
                  <p className="text-2xl font-bold text-indigo-400">{sleepAnalysis.average_quality?.toFixed(1) || '—'}</p>
                  <p className="text-gray-500 text-xs mt-1">Calidad promedio</p>
                </div>
                <div className="p-3 bg-indigo-500/10 rounded-xl">
                  <p className="text-2xl font-bold text-indigo-400">{sleepAnalysis.total_logs || 0}</p>
                  <p className="text-gray-500 text-xs mt-1">Registros</p>
                </div>
              </div>
              {sleepAnalysis.recommendations?.length > 0 && (
                <div>
                  <p className="text-gray-400 text-xs mb-2 font-medium">💡 Recomendaciones</p>
                  <ul className="space-y-1.5">
                    {sleepAnalysis.recommendations.map((r, i) => (
                      <li key={i} className="text-gray-300 text-xs flex items-start gap-2">
                        <span className="text-indigo-400 mt-0.5">•</span>{r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {sleepLogs.length > 0 && (
            <div className="glass-panel p-5 rounded-2xl">
              <h3 className="text-white font-semibold mb-3 text-sm">Historial de Sueño ({sleepLogs.length})</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
                {sleepLogs.map(log => (
                  <div key={log.id} className="flex items-center justify-between p-2.5 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Moon size={16} className="text-indigo-400" />
                      <div>
                        <p className="text-white text-xs font-medium">{log.date}</p>
                        <p className="text-gray-500 text-xs">{log.bedtime} → {log.wake_time}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm font-bold">{log.duration_hours?.toFixed(1)}h</p>
                      <p className="text-yellow-400 text-xs">{'⭐'.repeat(log.quality)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Síntomas ────────────────────────────────────────────────────── */}
      {activeTab === 'symptoms' && (
        <div className="space-y-5">
          {/* Search card */}
          <div className="glass-panel p-5 rounded-2xl">
            <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
              <Stethoscope size={16} className="text-red-400" />Análisis de Síntomas con IA
            </h3>
            <p className="text-gray-500 text-xs mb-4">Describe tus síntomas y nuestro agente doctor los analizará de forma profesional.</p>
            <textarea
              value={symptomInput}
              onChange={e => setSymptomInput(e.target.value)}
              placeholder="Ej: tengo dolor de cabeza intenso, fiebre de 38°C, dolor de garganta y cansancio desde hace 2 días..."
              rows={4}
              className="input-glass w-full text-sm resize-none rounded-xl mb-3"
            />
            <button
              onClick={analyzeSymptoms}
              disabled={analyzingSymptoms || !symptomInput.trim()}
              className="w-full py-2.5 rounded-xl font-semibold text-sm text-white transition-all flex items-center justify-center gap-2"
              style={{ background: analyzingSymptoms ? '#6b7280' : '#dc2626' }}>
              {analyzingSymptoms ? (
                <><RefreshCw size={15} className="animate-spin" />Analizando síntomas...</>
              ) : (
                <><Stethoscope size={15} />Analizar mis síntomas</>
              )}
            </button>
          </div>

          {/* Results card */}
          {symptomResult && (
            <div style={{ background: '#ffffff', color: '#111827', borderRadius: 16, padding: 20, boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #f3f4f6' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Stethoscope size={20} color="#dc2626" />
                </div>
                <div>
                  <p style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Diagnóstico del Agente Dr. OkBot</p>
                  <p style={{ fontSize: 12, color: '#6b7280' }}>Análisis basado en los síntomas descritos</p>
                </div>
              </div>

              {symptomResult.raw ? (
                <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{symptomResult.raw}</p>
              ) : (
                <>
                  {/* Symptoms detected */}
                  {symptomResult.sintomas_detectados?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <p style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 8 }}>🩻 Síntomas detectados</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {symptomResult.sintomas_detectados.map((s, i) => (
                          <span key={i} style={{ background: '#fef3c7', color: '#92400e', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500 }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Possible causes */}
                  {symptomResult.posibles_causas?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <p style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 8 }}>🔍 Posibles causas</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {symptomResult.posibles_causas.map((c, i) => {
                          const gravColors = { leve: { bg: '#dcfce7', text: '#166534' }, moderada: { bg: '#fef9c3', text: '#854d0e' }, grave: { bg: '#fee2e2', text: '#991b1b' } };
                          const col = gravColors[c.gravedad] || gravColors.leve;
                          return (
                            <div key={i} style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 12px', borderLeft: '3px solid #dc2626' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <p style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{c.causa}</p>
                                <span style={{ background: col.bg, color: col.text, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{c.gravedad}</span>
                              </div>
                              <p style={{ fontSize: 12, color: '#6b7280' }}>{c.descripcion}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Treatments */}
                  {symptomResult.tratamientos?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <p style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 8 }}>💊 Tratamientos sugeridos</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {symptomResult.tratamientos.map((t, i) => {
                          const urgColors = { inmediata: '#fee2e2', pronto: '#fef9c3', rutinaria: '#dcfce7' };
                          return (
                            <div key={i} style={{ background: urgColors[t.urgencia] || '#f9fafb', borderRadius: 10, padding: '10px 12px' }}>
                              <p style={{ fontWeight: 600, fontSize: 13, color: '#111827', marginBottom: 3 }}>{t.tipo}</p>
                              <p style={{ fontSize: 12, color: '#374151' }}>{t.descripcion}</p>
                              <span style={{ fontSize: 11, color: '#6b7280' }}>Urgencia: {t.urgencia}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Home care */}
                  {symptomResult.cuidados_en_casa?.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <p style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 8 }}>🏠 Cuidados en casa</p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {symptomResult.cuidados_en_casa.map((c, i) => (
                          <li key={i} style={{ display: 'flex', gap: 8, fontSize: 12, color: '#374151' }}>
                            <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>{c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* When to go to doctor */}
                  {symptomResult.cuando_ir_al_medico?.length > 0 && (
                    <div style={{ marginBottom: 16, background: '#fff7ed', borderRadius: 10, padding: '12px 14px', border: '1px solid #fed7aa' }}>
                      <p style={{ fontWeight: 700, fontSize: 13, color: '#9a3412', marginBottom: 8 }}>⚠️ Acude al médico si presentas</p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {symptomResult.cuando_ir_al_medico.map((s, i) => (
                          <li key={i} style={{ display: 'flex', gap: 8, fontSize: 12, color: '#9a3412' }}>
                            <span style={{ fontWeight: 700 }}>•</span>{s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Specialist */}
                  {symptomResult.especialista_recomendado && (
                    <div style={{ background: '#eff6ff', borderRadius: 10, padding: '10px 14px', marginBottom: 16, border: '1px solid #bfdbfe' }}>
                      <p style={{ fontSize: 13, color: '#1e40af' }}>
                        <span style={{ fontWeight: 700 }}>👨‍⚕️ Especialista recomendado: </span>{symptomResult.especialista_recomendado}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Disclaimer */}
              <div style={{ marginTop: 12, padding: '12px 14px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca' }}>
                <p style={{ fontSize: 11, color: '#991b1b', fontWeight: 600, marginBottom: 4 }}>⚕️ IMPORTANTE — Aviso Médico</p>
                <p style={{ fontSize: 11, color: '#b91c1c', lineHeight: 1.5 }}>
                  Este análisis es orientativo y no reemplaza la consulta con un médico profesional. 
                  <strong> Siempre acude a un médico certificado</strong> para recibir un diagnóstico oficial y tratamiento adecuado. 
                  <strong> Nunca te automediques</strong> sin supervisión médica.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <ShareModal isOpen={shareModal.open} onClose={() => setShareModal({ open: false, item: null, type: '' })}
        itemType={shareModal.type} itemId={shareModal.item?.id} itemData={{ userId: user.id }} />

      <AIAgentModule sectionContext="salud y bienestar" compact={true} placeholder="Consúltame sobre salud, medicamentos o bienestar..." />
    </div>
  );
};

export default HealthModule;
